import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertTriangle, ChevronRight, ExternalLink, Filter,
  Sparkles, Loader2, Copy, CheckCircle2, Mail, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { usePermissions } from "@/context/PermissionsContext";

const PRIO_STYLES = {
  critical: { chip: "bg-rose-100 text-rose-800 border-rose-200",    bar: "bg-rose-500",    label: "Critical" },
  high:     { chip: "bg-amber-100 text-amber-800 border-amber-200", bar: "bg-amber-500",   label: "High" },
  medium:   { chip: "bg-sky-100 text-sky-800 border-sky-200",       bar: "bg-sky-500",     label: "Medium" },
  low:      { chip: "bg-slate-100 text-slate-700 border-slate-200", bar: "bg-slate-400",   label: "Low" },
};

export function ExecutiveExceptionsPanel() {
  const { role } = usePermissions();
  const isExec = (role || "").toLowerCase() === "executive";
  const [data, setData] = useState(null);
  const [prio, setPrio] = useState("all");
  const [loading, setLoading] = useState(true);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [mutedKeys, setMutedKeys] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    api.get("/executive/exceptions/mutes")
      .then((r) => { setCatalogue(r.data.catalogue || []); setMutedKeys(r.data.muted_keys || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit_per_group: "15" });
    if (prio !== "all") p.set("priority", prio);
    api.get(`/executive/exceptions?${p}`)
      .then((r) => { setData(r.data); if (r.data?.muted_keys) setMutedKeys(r.data.muted_keys); })
      .catch(() => setData({ counts: {critical:0,high:0,medium:0,low:0}, total: 0, items: [] }))
      .finally(() => setLoading(false));
  }, [prio, reloadKey]);

  const toggleMute = async (key, nextShown) => {
    // nextShown === true means the exec wants it VISIBLE → muted:false
    setSavingKey(key);
    try {
      const r = await api.post("/executive/exceptions/mutes", { key, muted: !nextShown });
      setMutedKeys(r.data.muted_keys || []);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not update exception settings");
    } finally { setSavingKey(null); }
  };

  if (loading) return <div data-testid="exec-exceptions-panel" className="rounded-lg border border-slate-200 bg-white p-5 h-64 shimmer" />;

  const c = data?.counts || {};
  const items = data?.items || [];

  return (
    <div data-testid="exec-exceptions-panel" className="rounded-lg border border-slate-200 bg-white">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <div>
            <div className="font-display text-lg text-slate-900">Executive Exceptions</div>
            <div className="text-xs text-slate-500">Ranked attention list across every module</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          {["all", "critical", "high", "medium", "low"].map((p) => (
            <Button
              key={p}
              size="sm"
              data-testid={`exec-exc-filter-${p}`}
              variant={prio === p ? "default" : "outline"}
              onClick={() => setPrio(p)}
              className={`text-xs h-7 ${prio === p ? "bg-gbe hover:bg-gbe/90 text-white" : ""}`}
            >
              {p === "all" ? `All (${data?.total ?? 0})` : `${PRIO_STYLES[p].label} (${c[p] ?? 0})`}
            </Button>
          ))}
          {isExec && (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="text-xs h-7" data-testid="exec-exc-manage-btn">
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
                  Manage
                  {mutedKeys.length > 0 && (
                    <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 bg-slate-100 text-slate-600 border-slate-200">
                      {mutedKeys.length} hidden
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0" data-testid="exec-exc-manage-popover">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-sm font-medium text-slate-900">Show / hide exception types</div>
                  <div className="text-xs text-slate-500">Hidden types won't appear in this panel.</div>
                </div>
                <div className="py-1 max-h-72 overflow-y-auto">
                  {catalogue.map((cat) => {
                    const shown = !mutedKeys.includes(cat.key);
                    return (
                      <div key={cat.key} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                        <div className="min-w-0">
                          <div className="text-sm text-slate-800 truncate">{cat.label}</div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">{cat.module}</div>
                        </div>
                        <Switch
                          checked={shown}
                          disabled={savingKey === cat.key}
                          onCheckedChange={(v) => toggleMute(cat.key, v)}
                          data-testid={`exec-exc-mute-${cat.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-4 gap-2 text-xs">
        {["critical", "high", "medium", "low"].map((p) => (
          <div key={p} className="flex items-center gap-2">
            <div className={`w-1.5 h-6 rounded-full ${PRIO_STYLES[p].bar}`} />
            <div>
              <div className="text-slate-500 uppercase tracking-wider text-[10px]">{PRIO_STYLES[p].label}</div>
              <div className="font-display text-lg text-slate-900">{c[p] ?? 0}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Items */}
      <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-400">
            No exceptions in this scope 🎉
          </div>
        )}
        {items.map((it, idx) => {
          const s = PRIO_STYLES[it.priority] || PRIO_STYLES.low;
          const isInternal = it.source_link?.startsWith("/");
          return (
            <div key={`${it.type}-${it.id}-${idx}`} data-testid={`exec-exc-row-${it.id}`}
                 className="grid gap-2 px-5 py-3 items-center text-sm hover:bg-slate-50"
                 style={{ gridTemplateColumns: "auto 1fr 1fr auto auto" }}>
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${s.chip}`}>{s.label}</Badge>
              <div className="min-w-0">
                <div className="text-slate-900 font-medium truncate">{it.title}</div>
                <div className="text-xs text-slate-500 truncate">{it.description}</div>
              </div>
              <div className="min-w-0 text-xs text-slate-500">
                <span className="text-slate-400">{it.type}</span> · <span>{it.module}</span>
                {it.bu && <> · <span className="font-mono">{it.bu}</span></>}
                {it.owner && <> · <span>{it.owner}</span></>}
              </div>
              <div className="text-xs text-slate-500 text-right whitespace-nowrap">
                {it.exposure ? `$${Number(it.exposure).toLocaleString(undefined, {maximumFractionDigits: 0})}` : it.due_date || ""}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost"
                        data-testid={`exec-exc-resolve-${it.id}`}
                        title="AI resolve this exception"
                        className="h-7 px-2"
                        onClick={() => setResolveTarget(it)}>
                  <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                </Button>
                {it.source_link && (
                  isInternal ? (
                    <Link to={it.source_link} className="text-slate-400 hover:text-gbe" title="Open">
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <a href={it.source_link} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-gbe" title="Open">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ResolveDialog
        item={resolveTarget}
        open={!!resolveTarget}
        onOpenChange={(v) => { if (!v) setResolveTarget(null); }}
      />
    </div>
  );
}


// ============================================================
// AI resolve dialog
// ============================================================
function ResolveDialog({ item, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [contextHint, setContextHint] = useState("");
  const [copied, setCopied] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setResult(null); setContextHint(""); setSendTo(""); setCopied(false); setSending(false);
    } else if (item) {
      // Prefill recipient from owner if it looks like an email
      if (item.owner && /@/.test(item.owner)) setSendTo(item.owner);
    }
  }, [open, item]);

  if (!item) return null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/executive/exceptions/resolve", {
        priority: item.priority,
        type: item.type,
        module: item.module,
        title: item.title,
        description: item.description,
        client: item.client,
        owner: item.owner,
        due_date: item.due_date,
        exposure: item.exposure,
        source_link: item.source_link,
        context_hint: contextHint || null,
      });
      setResult(data);
    } catch (e) {
      toast.error("AI resolve failed", { description: formatApiErrorDetail(e) });
    } finally { setLoading(false); }
  };

  const copyMsg = async () => {
    if (!result?.draft_message) return;
    try {
      await navigator.clipboard.writeText(result.draft_message);
      setCopied(true); toast.success("Copied");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Copy failed"); }
  };

  const sendEmail = async () => {
    if (!result?.draft_message) return;
    if (!/@/.test(sendTo || "")) return toast.error("Enter a valid recipient email");
    setSending(true);
    try {
      // Dispatch through the most appropriate module endpoint based on source
      let path = null;
      const isContract = /^\/contracts/i.test(item.source_link || "");
      const isOpp = /^\/sales/i.test(item.source_link || "");
      const idMatch = (item.source_link || "").match(/id=([a-f0-9]{24})/i);
      const refId = idMatch ? idMatch[1] : item.id;

      if (isContract && refId) {
        path = `/contracts/${refId}/send-renewal-email`;
        await api.post(path, {
          to_email: sendTo,
          subject: `${item.title} — action required`,
          body: result.draft_message,
          contract_id: refId,
          kind: "contract_renewal",
        });
      } else if (isOpp && refId) {
        path = `/sales/opportunities/${refId}/send-message`;
        await api.post(path, {
          to_email: sendTo,
          subject: `${item.title} — action required`,
          body: result.draft_message,
          opportunity_id: refId,
          kind: "sales_next_action",
        });
      } else {
        // Fallback — open mail client with mailto
        window.open(
          `mailto:${sendTo}?subject=${encodeURIComponent(item.title + " — action required")}&body=${encodeURIComponent(result.draft_message)}`,
          "_blank",
        );
        toast.info("Opened your mail client (no direct-send endpoint for this exception).");
        return;
      }
      toast.success(`Email sent to ${sendTo}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Send failed", { description: formatApiErrorDetail(e) });
    } finally { setSending(false); }
  };

  const prioColour = { high: "text-rose-700", medium: "text-amber-700", low: "text-slate-600" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            AI resolve
          </DialogTitle>
          <DialogDescription>
            <span className="capitalize">{item.priority}</span> · {item.type} — {item.title}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="rounded bg-slate-50 border border-slate-200 text-xs px-3 py-2 text-slate-700">
              <div><b>Description:</b> {item.description || "—"}</div>
              {item.client && <div><b>Client:</b> {item.client}</div>}
              {item.owner && <div><b>Owner:</b> {item.owner}</div>}
              {item.due_date && <div><b>Due:</b> {item.due_date}</div>}
              {item.exposure ? (
                <div><b>Exposure:</b> ${Number(item.exposure).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
              ) : null}
            </div>
            <div>
              <label className="text-xs text-slate-500">Additional context (optional)</label>
              <Textarea rows={3} value={contextHint} onChange={(e) => setContextHint(e.target.value)}
                        placeholder="Anything the AI should know? e.g. 'client is a long-term partner', 'we've missed 2 renewal windows'…" />
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            <div className="flex items-center gap-3">
              <div className={`text-sm font-medium ${prioColour[result.priority]}`}>
                Priority: {result.priority}
              </div>
              {result.target_audience && (
                <div className="text-xs text-slate-500">Target: {result.target_audience}</div>
              )}
              {result.next_review_days != null && (
                <div className="text-xs text-slate-500">
                  Review in <b>{result.next_review_days}d</b> if unresolved
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Recommended actions</div>
              <ul className="list-disc list-inside text-sm text-slate-700 space-y-0.5">
                {result.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Draft message</div>
              <Textarea rows={9} value={result.draft_message || ""}
                        onChange={(e) => setResult({ ...result, draft_message: e.target.value })}
                        className="font-mono text-xs" />
            </div>
            <div className="text-[11px] text-slate-400">
              Model: {result.model} · Logged to AI activity
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button onClick={generate} disabled={loading}
                      className="bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Working…</> :
                          <><Sparkles className="w-4 h-4 mr-2" />Generate resolution</>}
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2">
                <Input type="email" value={sendTo}
                       onChange={(e) => setSendTo(e.target.value)}
                       placeholder="recipient@example.com"
                       data-testid="exec-exc-resolve-recipient"
                       className="flex-1" />
                <Button variant="outline" onClick={copyMsg}>
                  {copied ? <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Copied</> :
                            <><Copy className="w-4 h-4 mr-2" />Copy</>}
                </Button>
                <Button variant="ghost" onClick={() => setResult(null)}>Regenerate</Button>
                <Button onClick={sendEmail} disabled={sending}
                        data-testid="exec-exc-resolve-send"
                        className="bg-gbe hover:bg-gbe/90 text-white">
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</> :
                             <><Mail className="w-4 h-4 mr-2" />Send</>}
                </Button>
              </div>
              <div className="text-[11px] text-slate-400">
                Send goes via Resend and is logged to Audit Trail. Falls back to mailto when the exception isn't a Contract or Opportunity.
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
